import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";

// A Workers' Comp submission's prior coverage by year (PMS-S810, BRK-S389):
// one row a year, entered after "How many years of prior coverage?", each
// with its claims (PMS-S909, BRK-S505). PMS keeps them as the submission's
// prior-carrier rows and works out from them the years with the current
// carrier and the claims in the past 3 years, so neither is asked. The
// submission detail carries all of it; the PUT replaces the whole list and
// answers with it.

/** One year of prior coverage, as PMS keeps it. */
export interface PriorCoverageRow {
  id: string;
  /** The prior carriers list's carrier (GET /api/broker/prior-carriers); null
   *  on a row an admin typed as free text. */
  priorCarrierId: string | null;
  /** The listed carrier's name when it was written, or the name typed with
   *  the Other carrier. */
  carrierName: string | null;
  /** ISO YYYY-MM-DD. */
  effectiveDate: string;
  expirationDate: string;
  /** A plain number, in USD. */
  totalPremium: number | null;
  /** The year's claims (PMS-S909); null on a row saved before it. */
  claimCount: number | null;
}

/** One year as the portal writes it. A row echoing an id updates it in place;
 *  one without adds a row; a row left out goes. */
export interface PriorCoverageInput {
  id?: string;
  priorCarrierId: string;
  /** Required with the Other carrier, refused with any other. */
  carrierName?: string;
  effectiveDate: string;
  expirationDate: string;
  totalPremium: number;
  /** A whole number, 0 or more. */
  claimCount: number;
}

/** The rows, oldest term first, and what PMS works out from them: the
 *  years with the current carrier, null without a current carrier or rows;
 *  the claims in the past 3 years, Years 1 to 3's sum, and whether there
 *  were any, null without rows or while one of those has no count
 *  (PMS-S909). */
export interface PriorCoverage {
  priorCoverage: PriorCoverageRow[];
  yearsWithCurrentCarrier: number | null;
  wcClaimsTotal3Years: number | null;
  hadWcClaims3Years: boolean | null;
}

const BASE = "/api/broker/submissions";

/** PUT …/prior-coverage: the whole list. PMS takes rows only while the years
 *  question is asked and answered, as many as it says; `[]` clears them. An
 *  answer without its rows is a failure, never "none kept" (NN #7). */
export async function setPriorCoverage(
  request: AuthedRequester,
  id: string,
  rows: readonly PriorCoverageInput[],
): Promise<PriorCoverage> {
  const body = await request<Partial<PriorCoverage>>(
    `${BASE}/${encodeURIComponent(id)}/prior-coverage`,
    { method: "PUT", body: { priorCoverage: rows } },
  );
  if (!Array.isArray(body.priorCoverage)) {
    throw new ApiError(
      200,
      "We couldn’t read the prior coverage back — reload to check it.",
      body,
    );
  }
  return {
    priorCoverage: body.priorCoverage,
    yearsWithCurrentCarrier: body.yearsWithCurrentCarrier ?? null,
    wcClaimsTotal3Years: body.wcClaimsTotal3Years ?? null,
    hadWcClaims3Years: body.hadWcClaims3Years ?? null,
  };
}
